import { useApi } from '@src/app/context/Api';
import { User } from '@src/entities/User/ui/components/User';
import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import React, { useState } from 'react';

import { Styles } from './includes/styles';

/**
 * Button: DeleteNote
 *
 * Displays a button that triggers a modal popup to delete notes from the action path.
 *
 * @param actionPath
 * @returns {Element}
 * @constructor
 */
export function DeleteNote({ actionPath = {} }) {
  const [showModal, setShowModal] = useState(false);

  const { toast } = useToast();

  const { clearApiCache, useApiMutation } = useApi('ActionPath');
  const [SaveActionPathNote] = useApiMutation('SaveActionPathNote');

  /**
   * handleClose
   *
   * Closes the modal popup and resets the EID.
   */
  const handleClose = () => {
    setShowModal(false);
  };

  /**
   * handleSubmit
   *
   * Handles the form submission.
   *
   * @returns {Promise<void>}
   */
  const handleSubmit = async () => {
    handleClose();

    const toastId = 'actionPath-notes-remove';
    toast.show({
      ariaLoadingLabel: 'Removing Notes from Action Path',
      autoClose: false,
      id: `${toastId}-info`,
      isLoading: true,
      message: 'Action Path is preparing to remove the notes.',
      title: 'Removing Notes from Action Path...',
      variant: 'info',
    });

    const payload = {
      actionPathId: actionPath?.id,
      note: '',
    };

    await SaveActionPathNote(payload, {
      onError: (error) => {
        toast.hide(`${toastId}-info`);
        toast.show({
          id: `${toastId}-error`,
          message: 'Unable to remove notes.',
          title: 'Removing Notes Failed',
          variant: 'error',
        });
      },
      onSuccess: async () => {
        await clearApiCache([{ apiKey: 'ActionPath', queryKeys: ['GetActionPath'] }]);
        toast.hide(`${toastId}-info`);

        toast.show({
          id: `${toastId}-success`,
          message: 'Notes have been removed.',
          title: 'Removed Notes from Action Path',
          variant: 'success',
        });
      },
    });
  };

  return (
    <ErrorHandler location="src/features/ActionPaths/DeleteNote/DeleteNote.jsx">
      <User.Capability attributes={['write:note']} resource="ActionPaths">
        <Button
          after={<IconSymbol icon="delete" size="20" variant="outlined" />}
          onClick={() => {
            return setShowModal(true);
          }}
          variant="destructive"
        >
          Delete Note
        </Button>
      </User.Capability>

      <User.Capability attributes={['write:note']} method="hide" resource="ActionPaths">
        <Modal
          footer={
            <Modal.Section>
              <Divider height={1} />
              <Layout.Group alignLayout="right">
                <Button onClick={handleClose} variant="outline">
                  Cancel
                </Button>
                <Button onClick={handleSubmit} variant="destructive">
                  Delete
                </Button>
              </Layout.Group>
            </Modal.Section>
          }
          isOpen={showModal}
          onClose={handleClose}
          title="Delete Note"
        >
          <Styles>
            <Modal.Section>
              Are you sure you want to delete the note for Action Path <strong>{actionPath?.name}</strong>?
            </Modal.Section>
          </Styles>
        </Modal>
      </User.Capability>
    </ErrorHandler>
  );
}
